export interface Province {
  name: string;
  code: number;
  division_type: string;
  phone_code: number;
  codename: string;
  districts: District[];
}

export interface District {
  name: string;
  code: number;
  codename: string;
  division_type: string;
  province_code: number;
  wards?: any[];
}

class ProvincesService {
  private baseUrl = 'https://provinces.open-api.vn/api/v1';
  private cache: {
    provinces?: Province[];
    lastFetch?: number;
  } = {};

  // Cache duration: 1 hour
  private CACHE_DURATION = 60 * 60 * 1000;

  /**
   * Lấy danh sách tất cả tỉnh thành
   */
  async getProvinces(): Promise<Province[]> {
    // Check cache first
    if (this.cache.provinces && this.cache.lastFetch) {
      const now = Date.now();
      if (now - this.cache.lastFetch < this.CACHE_DURATION) {
        return this.cache.provinces;
      }
    }

    try {
      const response = await fetch(`${this.baseUrl}/?depth=2`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const provinces: Province[] = await response.json();
      
      // Update cache
      this.cache.provinces = provinces;
      this.cache.lastFetch = Date.now();
      
      return provinces;
    } catch (error) {
      console.error('Error fetching provinces:', error);
      throw new Error('Không thể tải danh sách tỉnh thành');
    }
  }

  /**
   * Lấy danh sách quận/huyện theo mã tỉnh
   */
  async getDistrictsByProvinceCode(provinceCode: number): Promise<District[]> {
    try {
      const provinces = await this.getProvinces();
      const province = provinces.find(p => p.code === provinceCode);
      return province?.districts || [];
    } catch (error) {
      console.error('Error fetching districts:', error);
      throw new Error('Không thể tải danh sách quận/huyện');
    }
  }

  /**
   * Tìm kiếm tỉnh thành theo tên
   */
  async searchProvinces(query: string): Promise<Province[]> {
    try {
      const response = await fetch(`${this.baseUrl}/p/search/?q=${encodeURIComponent(query)}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error searching provinces:', error);
      throw new Error('Không thể tìm kiếm tỉnh thành');
    }
  }

  /**
   * Tìm kiếm quận/huyện theo tên
   */
  async searchDistricts(query: string): Promise<District[]> {
    try {
      const response = await fetch(`${this.baseUrl}/d/search/?q=${encodeURIComponent(query)}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error searching districts:', error);
      throw new Error('Không thể tìm kiếm quận/huyện');
    }
  }

  /**
   * Lấy tỉnh thành theo mã
   */
  async getProvinceByCode(code: number): Promise<Province | null> {
    try {
      const provinces = await this.getProvinces();
      return provinces.find(p => p.code === code) || null;
    } catch (error) {
      console.error('Error getting province by code:', error);
      return null;
    }
  }

  /**
   * Lấy quận/huyện theo mã
   */
  async getDistrictByCode(code: number): Promise<District | null> {
    try {
      const provinces = await this.getProvinces();
      for (const province of provinces) {
        const district = province.districts.find(d => d.code === code);
        if (district) {
          return district;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting district by code:', error);
      return null;
    }
  }

  /**
   * Lấy tên tỉnh thành từ mã
   */
  async getProvinceName(code: number): Promise<string> {
    const province = await this.getProvinceByCode(code);
    return province?.name || '';
  }

  /**
   * Lấy tên quận/huyện từ mã
   */
  async getDistrictName(code: number): Promise<string> {
    const district = await this.getDistrictByCode(code);
    return district?.name || '';
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache = {};
  }
}

export const provincesService = new ProvincesService();
